import React, { useEffect, useState } from 'react';
import { Typography, Select } from 'antd';
import { message } from '../../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../../../contextProviders/UserProfileProvider';
import { updateMyUserProfile } from '../../../services/UserProfileService';
import { getCountries, ICountryResponse } from '../../../services/LocaleService';
import SettingsCard from '../../../components/Settings/SettingsCard';
import SettingsRowShell from '../../../components/Settings/SettingsRowShell';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title, Text } = Typography;

/**
 * "Configuración > Moneda" -- purely a display preference (see
 * currencyHelper's formatCurrency and the FX conversion shown alongside
 * listing prices). It never changes what a guest is actually charged: a
 * booking's real charge always freezes in the host's own currency at the
 * rate captured at CreateBooking time (Booking.GuestChargeAmount/
 * GuestCurrencyCode), regardless of what's picked here.
 */
const CurrencySection: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { userProfile, refresh } = useUserProfile();
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<ICountryResponse[]>([]);

  useEffect(() => {
    getCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  // Deduplicated by currencyCode -- getCountries() returns one row per
  // country, and many countries share a currency (e.g. every Eurozone one).
  const currencyOptions = Array.from(new Map(countries.map((c) => [c.currencyCode, c])).values())
    .filter((c) => !!c.currencyCode)
    .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode))
    .map((c) => ({ value: c.currencyCode, label: `${c.currencyCode} — ${c.currency}` }));

  const handleCurrencyChange = async (currencyCode: string) => {
    setSaving(true);
    try {
      await updateMyUserProfile({ preferredCurrencyCode: currencyCode });
      await refresh();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Redundant on mobile -- the list->detail header (Settings.tsx) already shows this section's name. */}
      {!isMobile && (
        <Title level={4} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.SETTINGS_NAV_CURRENCY)}
        </Title>
      )}

      <SettingsCard noMarginBottom>
        <SettingsRowShell
          left={<Text strong>{translate(t, TRANSLATIONS.SETTINGS_CURRENCY_LABEL)}</Text>}
          right={
            <Select
              style={{ width: 240 }}
              showSearch
              value={userProfile?.preferredCurrencyCode}
              loading={saving}
              onChange={handleCurrencyChange}
              filterOption={(input, option: any) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              options={currencyOptions}
            />
          }
        />
      </SettingsCard>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        {translate(t, TRANSLATIONS.SETTINGS_CURRENCY_HELP)}
      </Text>
    </div>
  );
};

export default CurrencySection;
